import { BlockUiService } from '../../../shared/services/blockUi/block-ui.service';
import { SubRepresentative } from './../../../core/models/user/sub-representative';
import { ErrorService } from '../../../core/services/error.service';
import { AccountService } from '../../../core/services/api/account.service';
import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { PopupService } from '../../../shared/services/popup/popup.service';
import { DefaultEmailPattern, ICMaskFormat, PassportFormat } from '../../../core/services/constant';
import { IdentificationType } from '../../../core/enum/identification-type.enum';
import { StorageService } from '../../../core/services/storage.service';
import { Role } from '../../../core/enum/role.enum';

@Component({
    selector: 'app-edit-representative',
    templateUrl: './edit-representative.component.html',
    styleUrls: ['./edit-representative.component.scss'],
    standalone: false
})
export class EditRepresentativeComponent implements OnInit {
  representatives: SubRepresentative[];
  showFormError: boolean = false;
  form: UntypedFormGroup;
  icMaskFormat = ICMaskFormat;
  isIssuer = false;
  isLoading = false;

  IdentificationType: typeof IdentificationType = IdentificationType;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private accountService: AccountService,
    private errorService: ErrorService,
    private blockUiService: BlockUiService,
    private popupService: PopupService,
    private storageService: StorageService,
  ) {}

  ngOnInit() {
    this.isIssuer = this.storageService.role == Role.IssuerRep;
    this.getRepresentatives();
  }

  getRepresentatives() {
    this.isLoading = true;

    this.accountService
      .getRepresentatives()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data) => {
          this.representatives = data;
          this.buildForm();

          if (this.representatives && this.representatives.length > 0) {
            this.representatives.forEach((rep) => {
              let repForm = this.buildRepForm();
              repForm.patchValue(rep);
              this.repFormControl.controls.unshift(repForm);
            });
          }
          // else {
          //   this.repFormControl.push(this.buildRepForm());
          // }
        },
        (err) => {
          this.errorService.showError(err);
        },
      );
  }

  buildForm() {
    this.showFormError = false;

    this.form = this.formBuilder.group({
      representatives: this.formBuilder.array([]),
    });
  }

  buildRepForm() {
    return this.formBuilder.group({
      id: [null],
      userIdentityNoType: [IdentificationType.MyKad, Validators.required],
      fullName: ['', Validators.required],
      email: [
        '',
        Validators.compose([Validators.required, Validators.pattern(DefaultEmailPattern)]),
      ],
      identityNo: ['', Validators.required],
    });
  }

  get repFormControl() {
    return <UntypedFormArray>this.form.controls.representatives;
  }

  addRep() {
    if (this.form.controls.representatives.invalid) {
      this.showFormError = true;
      return;
    }

    if (this.repFormControl.controls.length < 3) {
      this.repFormControl.push(this.buildRepForm());
      this.showFormError = false;
    }
  }

  removeRep(index: number) {
    this.popupService.confirm('settings.confirmDeleteRep').then(
      () => {
        this.repFormControl.removeAt(index);
      },
      () => {},
    );
  }

  isRemoveRepAllowed(index: number) {
    const groupField = this.repFormControl.controls[index]?.value;

    // [1] to ensure this is a new record
    // [2] to ensure the email entered in the record is NOT same as the login user
    if (
      groupField?.id === null ||
      (groupField?.email && groupField?.email !== this.storageService.email)
    ) {
      return true;
    }

    return false;
  }

  submit() {
    this.form.controls.representatives.updateValueAndValidity();

    if (this.form.invalid) {
      this.popupService.alert('common.missingFieldsAlert');
      this.showFormError = true;
      return;
    }

    this.representatives = this.form.value.representatives;
    this.blockUiService.open();

    this.accountService
      .updateAdditionalReps(this.representatives)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe(
        () => {
          this.popupService
            .success(
              this.isIssuer
                ? 'settings.issuerRepUpdateSuccess'
                : 'settings.corporateRepUpdateSuccess',
            )
            .then(() => {
              this.getRepresentatives();
            });
        },
        (err) => {
          this.errorService.showError(err);
        },
      );
  }

  changeIdentificationType(form: UntypedFormGroup) {
    let type = form.value.userIdentityNoType;

    if (type != IdentificationType.Passport) {
      form.controls.identityNo.setValidators([Validators.required]);
      form.controls.identityNo.updateValueAndValidity();
    } else {
      form.controls.identityNo.setValidators([
        Validators.required,
        Validators.pattern(PassportFormat),
      ]);
      form.controls.identityNo.updateValueAndValidity();
    }
  }
}
